import {
    Action,
    CardCollection,
    eq,
    Knot,
    Objekt,
    Promize,
    Table,
} from '@siposdani87/sui-js';

interface TableServiceLike {
    getTableOptions(): object;
    getTableFields(): string;
}

interface DataConfig {
    entityKey: string;
    fetchData: (params: Objekt) => Promize;
    extraParams?: (params: Objekt) => void;
}

function handleAction(
    dataConfig: DataConfig,
    service: TableServiceLike | null,
    setCount: (count: number) => void,
    setData: (items: Objekt[]) => void,
): (params: Objekt) => void {
    return (params: Objekt) => {
        if (service) {
            params.set('fields', service.getTableFields());
        }
        if (dataConfig.extraParams) {
            dataConfig.extraParams(params);
        }
        dataConfig.fetchData(params).then((result) => {
            const response = result as Objekt;
            setCount(response.get<number>('count'));
            setData(response.get<Objekt[]>(dataConfig.entityKey));
        });
    };
}

export function initTable(
    existing: Table | null,
    dom: Knot,
    selector: string,
    service: TableServiceLike | null,
    dataConfig: DataConfig,
    actions?: Action[],
    tabName?: string,
    activeTab?: string,
    tableOptions?: object,
): Table | null {
    if (tabName && !eq(activeTab!, tabName)) {
        return existing;
    }

    if (!existing) {
        const options = tableOptions || service!.getTableOptions();
        const table = new Table(dom, selector, options);

        if (actions) {
            table.setActions(actions);
        }

        table.on(
            'action',
            handleAction(
                dataConfig,
                service,
                (count) => table.setCount(count),
                (items) => table.setData(items),
            ),
        );

        table.render();
        return table;
    }

    existing.refresh();
    return existing;
}

export function initCardCollection(
    existing: CardCollection | null,
    dom: Knot,
    selector: string,
    service: TableServiceLike,
    dataConfig: DataConfig,
    cardKnotHandler: (cardKnot: Knot, item: Objekt) => void,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    formatters?: Record<string, Function>,
    options?: Record<string, unknown>,
    tabName?: string,
    activeTab?: string,
): CardCollection | null {
    if (tabName && !eq(activeTab!, tabName)) {
        return existing;
    }

    if (!existing) {
        const tableOptions = service.getTableOptions() as Record<
            string,
            unknown
        >;
        const collection = new CardCollection(
            dom,
            selector,
            formatters || {},
            {
                no_content: tableOptions['no_content'],
                row_count: 12,
                sort: { column: 'name', order: 'asc' },
                ...options,
            },
        );

        collection.on('cardKnot', cardKnotHandler);

        collection.on(
            'action',
            handleAction(
                dataConfig,
                service,
                (count) => collection.setCount(count),
                (items) => collection.setData(items),
            ),
        );

        collection.render();
        return collection;
    }

    existing.refresh();
    return existing;
}
